"use client";

import { useEffect, useRef, useState } from "react";
import { generateKundali } from "@/lib/api/kundali";
import type { KundaliRequest, KundaliResponse } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { trackCalculatorUsed } from "@/lib/analytics";
import RatingPrompt from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { NAKSHATRA_DATA, normalizeNakshatraKey } from "./_data";
import { NakshatraResultCard } from "./_components/NakshatraResultCard";

export default function NakshatraCalculatorClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const isHindi = defaultLanguage === "hi";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KundaliResponse | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    try {
      const response = await generateKundali({ ...data, skip_interpretation: true });
      setResult(response);
      trackCalculatorUsed({ calculator: "nakshatra-calculator" });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : isHindi ? "कुछ गड़बड़ हुई" : "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  }

  const nakshatraKey = result ? normalizeNakshatraKey(result.moon_nakshatra) : null;
  const nakshatraInfo = nakshatraKey ? NAKSHATRA_DATA[nakshatraKey] : null;
  const moonPlanet = result?.planets.find((p) => p.planet_id === "moon");

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <NakshatraHeader isHindi={isHindi} />

        {/* Form */}
        {!result && (
          <div className="mx-auto max-w-md">
            <BirthDetailsForm
              onSubmit={handleSubmit}
              loading={loading}
              submitLabel={isHindi ? "मेरा नक्षत्र बताएं" : "Find My Nakshatra"}
              error={error ?? undefined}
              language={defaultLanguage}
              analyticsToolName="nakshatra-calculator"
            />
          </div>
        )}

        {/* Result */}
        {result && (
          <div ref={resultRef}>
            <NakshatraResultCard
              result={result}
              nakshatraInfo={nakshatraInfo}
              moonPada={moonPlanet?.nakshatra_pada}
              isHindi={isHindi}
              onReset={() => {
                setResult(null);
                setError(null);
              }}
            />
          </div>
        )}

        {result && (
          <div className="mt-6">
            <RatingPrompt
              source={"tool_result" as never}
              mode="thumbs+reasons"
              reasons={RESULT_REASONS}
              prompt="Was this nakshatra result accurate?"
              context={{
                surface: "nakshatra_calculator_result",
                path: typeof window !== "undefined" ? window.location.pathname : null,
              }}
            />
          </div>
        )}

        <NakshatraSeoContent />

        {/* Cross Links */}
        <CalculatorCrossLinks currentPage="nakshatra-calculator" />
      </div>
    </div>
  );
}

function NakshatraHeader({ isHindi }: { isHindi: boolean }) {
  return (
    <div className="text-center mb-10">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        {isHindi ? "नक्षत्र गणना" : "Nakshatra Ganana"}
      </p>
      <h1
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        {isHindi ? "निःशुल्क नक्षत्र कैलकुलेटर" : "Free Nakshatra Calculator"}
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto text-ish-text-muted">
        {isHindi
          ? "अपनी जन्म तिथि दर्ज करें और तुरंत अपना जन्म नक्षत्र, पाद, स्वामी ग्रह, देवता और नामाक्षर जानें — 100% निःशुल्क।"
          : "Enter your date of birth and find your Janma Nakshatra (birth star), pada, ruling planet, deity, and name syllable — 100% free and instant."}
      </p>

      <div
        className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium"
        style={{ color: "var(--ish-text-faint)" }}
      >
        <TrustSignal icon="shield" label={isHindi ? "100% सटीक" : "100% Accurate"} />
        <TrustSignal icon="clock" label={isHindi ? "तुरंत परिणाम" : "Instant Results"} />
        <TrustSignal icon="user" label={isHindi ? "100% निःशुल्क" : "100% Free"} />
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        {icon === "clock" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        )}
        {icon === "user" && (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        )}
      </svg>
      {label}
    </span>
  );
}

function NakshatraSeoContent() {
  return (
    <div className="mt-12 card-sacred p-6 sm:p-8">
      <h2
        className="text-[20px] font-bold mb-4"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        What is a Nakshatra?
      </h2>
      <div className="space-y-3 text-[14px] leading-relaxed text-ish-text-muted">
        <p>
          <strong style={{ color: "var(--ish-dark)" }}>Nakshatras</strong> are the 27 lunar mansions
          in Vedic astrology. Each nakshatra spans 13 degrees and 20 minutes of the zodiac, and is
          further divided into 4 padas (quarters) of 3 degrees 20 minutes each. Your Janma Nakshatra
          (birth star) is determined by the Moon&apos;s position at your exact time of birth.
        </p>
        <p>
          Each nakshatra has a ruling planet (graha), a presiding deity (devata), and unique
          characteristics that influence your personality, temperament, and life path. The nakshatra
          system is fundamental to Vedic astrology and is used for muhurta (auspicious timing),
          compatibility matching, dasha calculations, and predictive astrology.
        </p>
        <p>
          The 27 nakshatras begin with Ashwini and end with Revati, covering the complete 360-degree
          zodiac. Knowing your nakshatra and pada is essential for accurate Vedic astrological
          analysis.
        </p>
      </div>
    </div>
  );
}
